import Moment from 'moment';
import { required, maxLength } from 'vuelidate/lib/validators';
import { Component, Mixins, Prop, Watch } from 'vue-property-decorator';

import { SECTION_TYPES } from '../../../../values/sections';
import EdInputModel from '../../../../../models/ed-input.model';
import EdFormModel from '../../../../../models/ed-form.model';
import KEYS from '@/values/keys';
import ToolService from '@/providers/tools';
import { Status } from '@/models/status.model';
import { IParamsModalConfirmation } from '@/plugins/ed-modal/interfaces/params-modal-confirmation.interface';
import { Section } from '@/courses/models/section.model';
import { COURSES_EVENTS } from '@/courses/values/courses-events.values';
import { TOAST_TYPES } from '@/plugins/ed-toast/values/ed-toast.values';
import { CORE_ROUTES } from '@/core/core.router';
import SectionSchoolCalendarMixin from '@/sections/mixins/section-school-calendar.mixin';
import { CourseToolsMixin } from '@/courses/mixins/course-tools.mixin';
import { SectionSchooolCalendarProvider } from '@/sections/providers/section-school-calendar.provider';
import { SectionProvider } from '@/sections/providers/section.provider';
import { SectionsProvider } from '@/content/providers/sections.provider';

@Component({
  name: 'ed-lesson-creator'
})
export default class EdLessonCreator extends Mixins(SectionSchoolCalendarMixin, CourseToolsMixin) {

  @Prop() readonly courseId: number;
  @Prop() readonly classId: number;
  @Prop() readonly isTemplate: boolean;

  public form: any = EdFormModel([
    EdInputModel('name', '', { required, maxLength: maxLength(60) },
      true
    ),
    EdInputModel('startDate', '')
  ])
  public saving: boolean = false;
  public autoGeneratedName: boolean = false;
  public convertToLessonActive: boolean = false;
  public status: Status = new Status();
public lessonDuration = EdInputModel('lessonDuration',  '');
  public isTooltipMultiline: boolean = false;
  private tooltipBreakpointLG: number = 1046;

  get name(): string {
    if (!this.section) {
      return this.$i18n.t('sections.content').toString();
    }
    if (this.section.fixedSection === 'FUN' || this.section.fixedSection === 'FILLERS' || this.section.fixedSection === 'ADDITIONAL') {
      return this.$i18n.t(`sections.${this.section.fixedSection.toLocaleLowerCase()}`).toString();
    } else {
      return ToolService.exitSpecialChars(this.section.name);
    }
  }

  get title() {
    if(this.editionMode) {
      return `${this.labelTitles.edit}: ${this.name}`; 
    } else {
      return `${this.labelTitles.addTo} ${this.name}`;
    }
  }

  get labelTitles() {
    if(this.isCreatingLesson) {
      return {
        addTo: this.$i18n.t('sections.lessonCreator.addToLesson'),
        edit: this.$i18n.t('sections.lessonCreator.editLesson'),
      }
    }
    return {
      addTo: this.$i18n.t('lesson.addTitleTo'),
      edit: this.$i18n.t('lesson.editTitle'),
    }
  }

  get isCreatingLesson() {
    return this.course.component.isLesson;
  }

  get sectionSubtitle(): string {
    let lessonLabel = this.$i18n.t('common.lesson').toString();
    if(this.isCreatingLesson && this.section && this.section.fixedSequence) {
      lessonLabel += ` ${this.section.fixedSequence}`;
      if(lessonLabel !== this.section.name) {
        return lessonLabel;
      }
    }
    return '';
  }

  get course() {
    return this.getCourseFromStore({courseId: this.courseId , classId: this.classId});
  }

  get editionMode() {
    return this.course.component.edition;
  }

  get section(): Section {
    if(!this.course.component.section) {
      return null;
    }
    return SectionsProvider.getSectionByID(
      this.course.sections,
      this.course.component.section.id
    );
  }

  get isCreatingInFunSection() {
    return this.course.component?.fixedSection === 'FUN';
  }

  get isCreatingInFillerSection() {
    if (this.section) {
      return this.section.fixedSection === 'FILLERS';
    }
    return false;
  }

  get sectionId() {
    return this.section ? this.section.sectionId : null;
  }

  get canUseDateName() {
    return this.form.startDate.value ? true : false;
  }

  get startSchoolDay() {
    return this.form.startDate.value ? SectionSchooolCalendarProvider.getSchoolDayIndex(this.form.startDate.value) : null;
  }

  get canUseConvertToLessonCheck() {
    return this.section && this.isTemplate && this.editionMode;
  }

  get canAddSuggestedTime() {
    if (this.isTemplate) {
      if (this.editionMode) {
        return this.convertToLessonActive;
      } 

      return this.isCreatingLesson;
    }

    return false;
  }

  get convertLessonCheckStatus(): { disabled: boolean, tooltip: string } {
    const result = { disabled: true, tooltip: '' };
    if(this.section) {
      if(this.section.fixedSection === 'LESSON') {
        const disabled = !SectionProvider.getIfFolderCanHaveObjects(this.section.sections);
        result.disabled = disabled;
        result.tooltip = disabled ? this.$i18n.t('sections.lessonCreator.cantConvertForFolder').toString(): '';
      } else {
        const hasParentLesson = SectionProvider.getHasParentLessons(this.course.sections, this.section);
        const hasChildLesson = SectionProvider.getHasSectionLessons(this.section.sections);
        result.disabled = hasParentLesson || hasChildLesson;
        result.tooltip = result.disabled ? this.$i18n.t('sections.lessonCreator.cantConvertForLesson').toString(): '';
      }
    }
    return result;
  }

  get isFormInvalid() {
    return !this.form.valid || this.saving || (!!this.lessonDuration.value && Number(this.lessonDuration.value) <= 0);
  }

  resetValidations() {
    this.$root.$emit('reset-text-field-validation');
  }

  init(name: string = '', startDate: string = '') {
    this.form = EdFormModel([
      EdInputModel('name', name, {
        required,
        maxLength: maxLength(60)
      },
        name ? true : false
      ),
      EdInputModel('startDate', startDate)
    ]);

    setTimeout(() => {
      this.resetValidations();
    }, 3);
  }

  goBack() {
    if(this.$listeners.goBack) {
      this.$emit('goBack'); 
      return
    } 
    const name = this.isTemplate ? CORE_ROUTES.CREATE_COURSE_TEMPLATE.NAME : CORE_ROUTES.CREATE_COURSE.NAME;
    this.$router.push({
      name,
      params: {
        id: this.course.id
      }
    });
  }

  async createSection() {
    if(!this.editionMode) {
      try {
        this.saving = true;
        await this.$Courses.Store.createSectionA({
          name: this.form.name.value,
          type: SECTION_TYPES.SECTION.code,
          parentID: this.sectionId,
          courseId: this.courseId,
          classId: this.classId,
          startSchoolDay: this.startSchoolDay,
          autoGeneratedName: this.autoGeneratedName,
          teacherOnly: false,
          startDate: this.form.startDate.value,
          fixedSection: this.isCreatingLesson ? 'LESSON' : null,
          lessonDuration: this.lessonDuration.value,
        });
        
        await this.getContentForCourse({ courseId: this.courseId, classId:this.classId });
        this.cleanForm();
        this.saving = false;
      } catch(err) {
        this.$EdToast.create({ 
          msg:  `${this.$t('courses.errors.errorSavingObject')}`,
          type: TOAST_TYPES.DANGER,
          top: '3rem',
          timeout: 3000
        });
        this.saving = false;
      }
    } else {
      this.updateLesson();
    }
  }

  updateLesson() {
    let isAfterOthersDate = this.getIsAfterOtherDates(this.section);
    if(isAfterOthersDate) {
      let params: IParamsModalConfirmation = {
        body: this.$i18n.t('courses.objects.moveObjectDueDate').toString(), 
        actionText: this.$i18n.t('common.accept').toString(),
        firstLoadingIsDone: true,
        action: async () => {
          await this.doUpdateLesson(true);
          this.$edEventBus.$emit(COURSES_EVENTS.REFRESH_WIDGET_COURSE, { 
            courseId: this.section.courseId,
            classId: this.section.classId
          });
          this.$EdModal.closeModal();
        },
        secondActionText: this.$i18n.t('common.cancel').toString(),
        secondAction: () => {}
      };
      this.$EdModal.createConfirmationModal(params);
    } else {
      this.doUpdateLesson();
    }
  }

  cleanForm() {
    this.form.set('name', '');
    this.form.set('startDate', '');
    this.lessonDuration.value = '';
  }

  async doUpdateLesson(hasToRecalculate = false) {
    let data: any = { ...this.section };
    
    data.name = this.form.name.value;
    data.autoGeneratedName = this.autoGeneratedName;
    data.recalculateDueDate = hasToRecalculate;
    data.recalculateLastDate = hasToRecalculate;
    data.submissionDates = {
      startSchoolDay: this.startSchoolDay,
      dueDateAuto: true,
      lastDateAuto: true
    };
    data.homeworkPoints = this.section.homeworkPoints ? this.section.homeworkPoints : 0;
    data.homeworkWeight = this.section.homeworkWeight ? this.section.homeworkWeight : 0;
    data.fixedSection = this.convertToLessonActive ? 'LESSON' : '';
    data.lessonDuration = this.lessonDuration.value;

    this.saving = true;
    try{
      await this.$Courses.Store.updateSectionA(data);
      await this.getContentForCourse({ courseId: this.courseId, classId: this.classId});
    } catch(error){
      this.$EdToast.create({ 
        msg:  `${this.$t('courses.errors.errorSavingObject')}`,
        type: TOAST_TYPES.DANGER,
        top: '3rem',
        timeout: 3000
      });
    } finally {
      this.saving = false;
    }
  }

  getIsAfterOtherDates(section: Section) {
    let isAfterOtherDate = false;
    if(section.sections) {
      let sections: Section[] = section.sections;
      sections.forEach(s => {
        let dates = s.submissionDates;
        if(dates) {
          if(dates.startSchoolDay && this.startSchoolDay > dates.startSchoolDay) {
            isAfterOtherDate = true;
          }
          if(dates.dueSchoolDay && this.startSchoolDay > dates.dueSchoolDay) {
            isAfterOtherDate = true;
          }
        }
        if(!isAfterOtherDate && s.sections) {
          isAfterOtherDate = this.getIsAfterOtherDates(s);
        }
      });
    }
    return isAfterOtherDate;
  }

  getDefaultName() {
    if(this.isCreatingLesson) {
      const sections = this.course.sections.filter(s => {
        if (!this.course.component?.fixedSection) {
          return s.fixedSection === null || s.fixedSection === 'LESSON';
        } else {
          return s.fixedSection === this.course.component?.fixedSection;
        }
      });

      const lessonCount = SectionProvider.getNextLessonSequence(sections, this.section?.id || null);

      return `${this.$i18n.t('common.lesson')} ${lessonCount + 1}`;
    }

    return '';
  }

  setData() {
    this.status.setLoading();
    if(this.editionMode && this.section) {
      let dates = this.section.submissionDates || {};
      this.init(
        this.section.name,
        SectionSchooolCalendarProvider.getSchoolDayDate(dates.startSchoolDay)
      );
      this.convertToLessonActive = this.section.fixedSection === 'LESSON';
      this.autoGeneratedName = this.section.autoGeneratedName;
      this.lessonDuration.value = this.section.lessonDuration ? this.section.lessonDuration.toString() : '';
    } else {
      this.init(this.getDefaultName());
      
      if (this.course.suggestedLessonDuration && this.isCreatingLesson) {
        this.lessonDuration.value = this.course.suggestedLessonDuration.toString();
      }
    }
    this.status.setLoad();
  }

  onResize() {
    this.isTooltipMultiline = window.innerWidth <= this.tooltipBreakpointLG;
  }

  created() {
    this.setData();
  }

  mounted() {
    this.onResize();
    window.addEventListener('resize', this.onResize);
  }

  onStartDateChanged(date: string) {
    if(this.autoGeneratedName && this.status.load) {
      if(date) {
        this.form.set(
          'name',
          Moment(date).format('ll')
        );
        this.form.setValid('name', true);
      } else {
        this.autoGeneratedName = false;
      }
    }
  }

  @Watch('autoGeneratedName', { immediate: false })
  autoGeneratedNameChanged() {
    this.onStartDateChanged(this.form.startDate.value);
  }

  @Watch('form.name', { deep: true, immediate: false })
  formChanged(name) {
    if(this.autoGeneratedName && this.status.load) {
      if(name.value !== Moment(this.form.startDate.value).format('ll')) {
        this.autoGeneratedName = false;
      }
    }
  }

  @Watch('section', { deep: true, immediate: false })
  sectionChanged() {
    this.cleanForm();
    this.setData();
    setTimeout(() => {
      this.resetValidations();
    }, 100);
  }

}
